import {
  EDGELESS_HANDLE_CLEARANCE,
  EDGELESS_HANDLE_GEOMETRY,
} from '@blocksuite/affine-shared/consts';
import { type IVec, rotatePoint } from '@blocksuite/global/gfx';

export type EdgelessDragHandleKind = 'hover' | 'selection';

export type EdgelessDragHandleTarget = {
  anchor: {
    left: number;
    rotate: number;
    top: number;
  };
  elementIds: readonly string[];
  key: string;
  kind: EdgelessDragHandleKind;
  rect: {
    height: number;
    left: number;
    top: number;
    width: number;
  };
};

export function resolveEdgelessDragHandleAnchor(input: {
  height: number;
  left: number;
  rotate: number;
  top: number;
  width: number;
}) {
  const centerX = input.left + input.width / 2;
  const centerY = input.top + input.height / 2;
  const [left, top] = rotatePoint(
    [centerX, input.top],
    [centerX, centerY],
    input.rotate
  );

  return {
    left,
    rotate: input.rotate,
    top,
  };
}

export type EdgelessDragHandleSlot = {
  target: EdgelessDragHandleTarget | null;
  visible: boolean;
};

export function resolveEdgelessDragHandleVisibility(input: {
  draggingKind: EdgelessDragHandleKind | null;
  hoverTarget: EdgelessDragHandleTarget | null;
  presentation: boolean;
  selectionTarget: EdgelessDragHandleTarget | null;
}): {
  hover: boolean;
  selection: boolean;
} {
  if (input.presentation) {
    return { hover: false, selection: false };
  }

  const sameTarget =
    input.hoverTarget !== null &&
    input.selectionTarget !== null &&
    input.hoverTarget.key === input.selectionTarget.key;
  const selection =
    input.selectionTarget !== null &&
    !(input.draggingKind === 'hover' && sameTarget);
  const hover =
    input.hoverTarget !== null &&
    (input.draggingKind === 'hover' ||
      (input.selectionTarget?.elementIds.length ?? 0) <= 1) &&
    (input.draggingKind === 'hover' || !sameTarget);

  return { hover, selection };
}

export function resolveEdgelessDragHandleMoveIds(input: {
  kind: EdgelessDragHandleKind;
  target: EdgelessDragHandleTarget;
}): readonly string[] {
  return input.kind === 'hover'
    ? input.target.elementIds.slice(0, 1)
    : input.target.elementIds;
}

export const EDGELESS_HANDLE_QUERY_RADIUS = Math.hypot(
  EDGELESS_HANDLE_GEOMETRY.width / 2,
  EDGELESS_HANDLE_CLEARANCE
);

/** The corridor between an element edge and its handle. */
export function isPointInEdgelessHandleZone(
  point: IVec,
  anchor: EdgelessDragHandleTarget['anchor']
): boolean {
  const [x, y] = rotatePoint(point, [anchor.left, anchor.top], -anchor.rotate);
  return (
    Math.abs(x - anchor.left) <= EDGELESS_HANDLE_GEOMETRY.width / 2 &&
    y >= anchor.top - EDGELESS_HANDLE_CLEARANCE &&
    y <= anchor.top
  );
}
